/* One measured scroll frame per visible chapter. All essential copy is in Liquid. */
(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = value => { const x = clamp(value); return x * x * (3 - 2 * x); };
  class ProductJourney extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-journey-step]')];
      this.pieces = Object.fromEntries([...this.querySelectorAll('[data-journey-piece]')].map(el => [el.dataset.journeyPiece, el]));
      this.video = this.querySelector('[data-journey-video]');
      this.playButton = this.querySelector('[data-journey-play]');
      this.playLabel = this.querySelector('[data-journey-play-label]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.filmProgress = 0;
      const options = { passive: true, signal: this.abort.signal };
      this.schedule = () => { if (this.near && !this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      addEventListener('scroll', this.schedule, options);
      addEventListener('resize', this.schedule, options);
      this.motion.addEventListener('change', this.schedule, { signal: this.abort.signal });
      this.observer = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        if (this.near) { this.loadFilm(); this.schedule(); } else this.video?.pause();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
      if (this.video) {
        this.video.addEventListener('loadedmetadata', this.schedule, { signal: this.abort.signal });
        this.video.addEventListener('seeked', () => { if (!this.manualPlayback) this.seekFilm(); }, { signal: this.abort.signal });
        this.video.addEventListener('ended', () => { this.manualPlayback = false; this.playLabel.textContent = 'Relire l’animation'; }, { signal: this.abort.signal });
        this.playButton.hidden = false;
        this.playButton.addEventListener('click', () => {
          if (this.manualPlayback) { this.video.pause(); this.manualPlayback = false; this.playLabel.textContent = 'Lire l’animation'; this.schedule(); return; }
          this.manualPlayback = true;
          this.classList.add('is-film');
          this.video.controls = true;
          this.video.currentTime = 0;
          this.video.play().then(() => { this.playLabel.textContent = 'Reprendre au défilement'; }).catch(() => { this.manualPlayback = false; });
        }, { signal: this.abort.signal });
      }
      this.classList.add('is-enhanced');
    }
    loadFilm() {
      const source = this.video?.querySelector('[data-src]');
      if (source) { source.src = source.dataset.src; source.removeAttribute('data-src'); this.video.preload = 'auto'; this.video.load(); }
    }
    seekFilm() {
      if (!this.video || this.manualPlayback || this.motion.matches || this.video.seeking || !Number.isFinite(this.video.duration)) return;
      const time = this.filmProgress * Math.max(0, this.video.duration - 0.05);
      if (Math.abs(this.video.currentTime - time) > 0.035) this.video.currentTime = time;
    }
    update() {
      const rect = this.getBoundingClientRect();
      const viewport = innerHeight;
      const center = viewport * (innerWidth < 750 ? 0.75 : 0.53);
      const stepRects = this.steps.map(el => el.getBoundingClientRect());
      let active = 0;
      stepRects.forEach((r, i) => { if (r.top < center) active = i; });
      this.steps.forEach((el, i) => { el.classList.toggle('is-active', i === active); if (i === active) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current'); });
      if (this.motion.matches) {
        Object.values(this.pieces).forEach(el => { el.removeAttribute('transform'); el.style.opacity = ''; });
        this.querySelector('.hj-product').style.transform = '';
        this.classList.remove('is-film');
        this.video?.pause();
        if (this.video) this.video.controls = true;
        return;
      }
      const p = clamp((center - rect.top) / Math.max(1, rect.height - viewport * 0.45));
      if (this.dataset.product === 'hynox') {
        // The photographed parts separate in sequence, without stacked whole bottles.
        const cap = smooth(p / 0.23);
        const straw = smooth((p - 0.16) / 0.30);
        const filter = smooth((p - 0.40) / 0.30);
        const spread = smooth(p / 0.78);
        this.pieces.body.setAttribute('transform', `rotate(${10 * (1-spread)} 430 1000)`);
        this.pieces.cap.setAttribute('transform', `translate(${-98*(1-cap)} ${155*(1-cap)}) rotate(${-9*(1-cap)} 500 500)`);
        this.pieces.straw.setAttribute('transform', `translate(${310*(1-straw)} ${260*(1-straw)}) rotate(${10*(1-straw)} 180 700)`);
        this.pieces.filter.setAttribute('transform', `translate(${-290*(1-filter)} ${100*(1-filter)})`);
        this.pieces.straw.style.opacity = smooth(straw / 0.32);
        this.pieces.filter.style.opacity = smooth(filter / 0.32);
        this.querySelector('.hj-product').style.transform = `scale(${1.12 - spread * 0.12})`;
      } else {
        const close = smooth(p / 0.43);
        this.pieces.cap.setAttribute('transform', `translate(0 ${-115*(1-close)}) rotate(${12*(1-close)} 625 300)`);
        this.querySelector('.hj-product').style.transform = `scale(${1.15 - close * 0.15})`;
        this.filmProgress = clamp((p - 0.46) / 0.48);
        this.classList.toggle('is-film', this.manualPlayback || p > 0.46);
        if (!this.manualPlayback) { this.video.controls = false; this.seekFilm(); }
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); this.video?.pause(); }
  }
  if (!customElements.get('hydros-product-journey')) customElements.define('hydros-product-journey', ProductJourney);
  // Holding a photo shows its alternate view; vertical swiping remains native.
  document.querySelectorAll('.hs-shop .ecom-product-card__media').forEach(link => {
    const restore = () => link.classList.remove('is-touch-preview');
    link.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch') return;
      link.classList.add('is-touch-preview');
    }, { passive: true });
    link.addEventListener('pointerup', restore, { passive: true });
    link.addEventListener('pointercancel', restore, { passive: true });
  });
})();
